import { useMemo } from 'react'
import { useFilterFields } from 'shared/hooks'
import useStyleMaps from 'site/Renderer/hooks/useStyleMap'
import { useSitemodel } from 'sitemodel/shared/hooks'
import { FilterForm } from './components'
import {
  FilterFieldsConfigContextProvider,
  FilterFieldsStylingContextProvider
} from './context'

import { useBoundFilterQueryElement } from './hooks/useBoundFilterQueryElement'
import useDefaultExecutedFilteraction from './hooks/useDefaultExecutedFilteraction'
import { useFilterDisplayOptions } from './hooks/useFilterDisplayOptions'
import { useFilterFormActions } from './hooks/useFilterFormActions'
import { useFilterFormInitialData } from './hooks/useFilterFormInitialData'
import { useFilterObjectclassSync } from './hooks/useFilterObjectclassSync'
import type { FilterFieldsProps } from './FilterFields.types'

const FilterFields = ({ props }: FilterFieldsProps) => {
  const {
    elementUuid,
    uuid_objectclass: objectclassUuid,
    uuid_classification: classificationUuid,
    defaultExecutedFilteraction,
    navigationslider_background_color: navigationsliderBackgroundColor,
    navigationslider_color: navigationsliderColor,
    filter_area_layout: filterAreaLayout,
    field_settings: fieldSettings,
    concat_fields: concatFields,
    filter_query_target_uuid: filterQueryTargetUuid,
    styleMap,
    filteraction_options: filteractionOptions,
    merge_filter_query: mergeFilterQuery,
    enable_composition: enableComposition,
    embedded,
    select_and_link_target_path: selectAndLinkTargetPath,
    depends_on_filteraction_options: dependsOnFilteractionOptions,
    hide_filter_fields: hideFilterFields
  } = props

  const sitemodel = useSitemodel()

  const styleProp = useStyleMaps(styleMap)

  const {
    filteractions: { hide_filteraction: hideFilteraction = false } = {},
    hide_main_collapsible_container: hideMainCollapsibleContainer = false,
    hide_fields_collapsible_container: hideFieldsCollapsibleContainer = false
  } = filterAreaLayout || {}

  const { fields } = useFilterFields({
    objectclassUuid,
    classificationUuid,
    filterAreaLayout,
    fieldSettings,
    hideFilterFields: !!hideFilterFields
  })

  const { onClickFilter, onClearFilterQuery } = useFilterFormActions({
    fields,
    filterQueryTargetUuid,
    objectclassUuid,
    mergeFilterQuery
  })

  const { displayOptions } = useFilterDisplayOptions({
    elementUuid,
    hideFilteraction,
    hideMainCollapsibleContainer,
    hideFieldsCollapsibleContainer,
    hideFilterFields
  })

  useFilterObjectclassSync({ objectclass: fields.objectclass })

  useBoundFilterQueryElement({ filterQueryTargetUuid })

  const { initialGroupedFieldValues, key } = useFilterFormInitialData({
    fields
  })

  const defaultExecutedFilteractionValue = useDefaultExecutedFilteraction(
    defaultExecutedFilteraction
  )

  const configValue = useMemo(
    () => ({
      elementUuid,
      uuidCurrentSitemodelObjectclass: objectclassUuid,
      filterAreaLayout,
      fieldSettings,
      concatFields,
      filteractionOptions,
      dependsOnFilteractionOptions,
      selectAndLinkTargetPath,
      embedded,
      hideFields: !!hideFilterFields,
      enableComposition,
      displayOptionsForElement: displayOptions?.[elementUuid]
    }),
    [
      elementUuid,
      objectclassUuid,
      filterAreaLayout,
      fieldSettings,
      concatFields,
      filteractionOptions,
      dependsOnFilteractionOptions,
      selectAndLinkTargetPath,
      embedded,
      hideFilterFields,
      enableComposition,
      displayOptions
    ]
  )

  const stylingValue = useMemo(
    () => ({
      navigationSliderBackgroundColor: navigationsliderBackgroundColor,
      navigationSliderColor: navigationsliderColor,
      style: styleProp
    }),
    [navigationsliderBackgroundColor, navigationsliderColor, styleProp]
  )

  const sitemodelTitle = sitemodel?.title
  const objectclassName = fields?.objectclass?.des

  if (!fields.classifiedFields[0].length) return null

  return (
    <FilterFieldsConfigContextProvider value={configValue}>
      <FilterFieldsStylingContextProvider value={stylingValue}>
        <FilterForm
          key={key}
          initialGroupedFieldValues={initialGroupedFieldValues}
          sections={fields.classifiedFields}
          onSubmit={onClickFilter}
          title={sitemodelTitle || objectclassName}
          defaultExecutedFilteraction={defaultExecutedFilteractionValue}
          clearFilterQuery={onClearFilterQuery}
        />
      </FilterFieldsStylingContextProvider>
    </FilterFieldsConfigContextProvider>
  )
}

export default FilterFields
